/**
 * Profile section config for Pepagora-style left nav.
 * Sections are grouped for display; each section shows a subset of ProfileData fields or a seller-only block.
 * Business Profile (seller) uses full section set; buyers see a reduced set.
 */

export type ProfileSectionSource = "userInfo" | "companyInfo" | "socialLinks";

export type SellerBlockId = "password";

/** Custom block for sections that need repeatable/checkbox group UI (seller Business Profile) */
export type BusinessProfileCustomBlockId =
  | "contact"
  | "shippingPayment"
  | "brandingExtended"
  | "tradeAdditional"
  | "factoryExtended";

export interface ProfileSectionDef {
  id: string;
  label: string;
  /** Optional description shown under section title */
  description?: string;
  /** Parent group for left nav grouping (e.g. "Business Information") */
  groupLabel?: string;
  /** When source is profile: which part of ProfileData and which keys */
  source?: ProfileSectionSource;
  fieldKeys?: string[];
  /** When source is seller block: custom UI block (Password, Payment, Addresses) */
  sellerBlock?: SellerBlockId;
  /** Custom block for Business Profile (checkbox group, repeatable, etc.) */
  customBlock?: BusinessProfileCustomBlockId;
  /** Show this section only for seller role */
  sellerOnly?: boolean;
  /** "All fields are Required" / "Optional" etc. */
  requiredLabel?: string;
}

/** Full Business Profile sections (seller) — matches pepagora reference exactly */
export const BUSINESS_PROFILE_SECTIONS: ProfileSectionDef[] = [
  // --- Business Information ---
  {
    id: "business-details",
    label: "Business Details",
    groupLabel: "Business Information",
    description:
      "Showcase your identity, operations, and presence — build buyer confidence. Legal owner name matches your registered profile and cannot be edited here.",
    source: "companyInfo",
    requiredLabel: "All fields are Required",
    fieldKeys: [
      "businessName",
      "legalOwnerName",
      "businessType",
      "employeeCount",
      "industry",
      "foundedYear",
      "industryTypes",
    ],
  },
  {
    id: "contact-information",
    label: "Contact Information",
    groupLabel: "Business Information",
    source: "companyInfo",
    requiredLabel: "All fields are Required",
    customBlock: "contact",
    fieldKeys: [
      "companyCountry",
      "companyAddress",
      "companyState",
      "companyCity",
      "companyPostalCode",
      "companyPhone",
      "companyEmail",
    ],
  },
  {
    id: "company-registration",
    label: "Company Registration Details",
    groupLabel: "Business Information",
    source: "companyInfo",
    requiredLabel: "At least one required (GST, PAN, or IEC)",
    fieldKeys: ["gstNumber", "gstDocumentUrl", "panNumber", "panDocumentUrl", "iecNumber", "iecDocumentUrl"],
  },
  {
    id: "about-company",
    label: "About your company",
    groupLabel: "Business Information",
    description:
      "Tell buyers what you do, your strengths, and what makes your business stand out. Shown on your public seller profile.",
    source: "companyInfo",
    fieldKeys: ["description"],
  },
  // --- Branding & Media (Optional) ---
  {
    id: "branding-media",
    label: "Branding & Media (Optional)",
    description: "Define brand identity, material and quality.",
    source: "companyInfo",
    customBlock: "brandingExtended",
    fieldKeys: [
      "companyLogo",
      "companyWebsite",
      "companyVideoUrl",
      "companyBrochures",
      "ownBrands",
      "certificatesQuality",
      "facebookUrl",
      "youtubeUrl",
      "instagramUrl",
      "linkedinUrl",
    ],
  },
  // --- Trade Information ---
  {
    id: "market-logistics",
    label: "Market & Logistics",
    groupLabel: "Trade Information",
    description: "Define details, quantity, material and quality.",
    source: "companyInfo",
    fieldKeys: ["mainMarkets"],
  },
  {
    id: "shipping-payment",
    label: "Shipping & Payment Terms",
    groupLabel: "Trade Information",
    source: "companyInfo",
    customBlock: "shippingPayment",
    fieldKeys: [
      "acceptedPaymentCurrency",
      "paymentMethods",
      "paymentTerms",
    ],
  },
  {
    id: "trade-additional",
    label: "Additional Information (Optional)",
    groupLabel: "Trade Information",
    source: "companyInfo",
    customBlock: "tradeAdditional",
    fieldKeys: [
      "nearestPort",
      "averageLoadTimeValue",
      "averageLoadTimeUnit",
      "acceptedDeliveryTerms",
    ],
  },
  // --- Factory & Warehouse Details ---
  {
    id: "factory-warehouse",
    label: "Factory & Warehouse Details",
    description:
      "Showcase your factory setup, production capacity, and storage details.",
    source: "companyInfo",
    customBlock: "factoryExtended",
    fieldKeys: [
      "totalFactorySize",
      "infrastructureImages",
      "qcStaffCount",
    ],
  },
  // --- Seller-only blocks (custom UI) ---
  {
    id: "password",
    label: "Password",
    sellerOnly: true,
    sellerBlock: "password",
  },
];

/** Buyer profile sections: Profile (name, email, country, logo) and Account (password) only. */
export const BUYER_PROFILE_SECTIONS: ProfileSectionDef[] = [
  {
    id: "personal",
    label: "Profile",
    groupLabel: "Profile",
    description:
      "Your name, email, country, and profile picture. Phone, WhatsApp, and website are shared with sellers when you send an enquiry — leave blank if not applicable.",
    source: "userInfo",
    fieldKeys: [
      "profilePic",
      "firstName",
      "lastName",
      "email",
      "country",
      "phone",
      "whatsapp",
      "website",
    ],
  },
  {
    id: "password",
    label: "Password",
    groupLabel: "Account",
    sellerBlock: "password",
  },
];

/** Sidebar groups for buyer profile — Profile and Account only. */
export const BUYER_PEPAGORA_GROUPS = [
  { label: "Profile", matchGroupLabel: "Profile" },
  { label: "Account", matchSellerBlock: true },
] as const;

/** Legacy/buyer profile sections (simpler set) */
export const PROFILE_SECTIONS: ProfileSectionDef[] = [
  {
    id: "business-details",
    label: "Business Details",
    groupLabel: "Business Information",
    description:
      "Showcase your identity, operations, and presence — build buyer confidence.",
    source: "companyInfo",
    fieldKeys: [
      "companyName",
      "companyBusinessType",
      "employeeCount",
      "industry",
      "foundedYear",
    ],
  },
  {
    id: "company-registration",
    label: "Company Registration Details",
    groupLabel: "Business Information",
    source: "companyInfo",
    fieldKeys: [
      "country",
      "address",
      "state",
      "city",
      "postalCode",
      "companyPhone",
      "companyEmail",
      "gstNumber",
      "panNumber",
      "taxId",
      "registrationNumber",
    ],
  },
  {
    id: "personal",
    label: "Personal & Contact",
    description: "Legal owner and primary contact.",
    source: "userInfo",
    fieldKeys: ["profilePic", "firstName", "lastName", "email", "phone", "whatsapp", "website", "bio"],
  },
  {
    id: "branding-media",
    label: "Branding & Media (Optional)",
    description: "Define details, quantity, material and quality.",
    source: "companyInfo",
    fieldKeys: ["companyLogo", "companyWebsite", "facebookUrl", "linkedinUrl", "twitterUrl"],
  },
  {
    id: "market-logistics",
    label: "Market & Logistics",
    groupLabel: "Trade Information",
    description: "Define details, quantity, material and quality.",
    source: "companyInfo",
    fieldKeys: ["country", "address", "state", "city", "postalCode"],
  },
  {
    id: "shipping-payment",
    label: "Shipping & Payment Terms",
    groupLabel: "Trade Information",
    source: "companyInfo",
    fieldKeys: [],
  },
  {
    id: "trade-additional",
    label: "Additional Information (Optional)",
    groupLabel: "Trade Information",
    source: "companyInfo",
    fieldKeys: ["description"],
  },
  {
    id: "factory-warehouse",
    label: "Factory & Warehouse Details",
    description:
      "Showcase your factory setup, production capacity, and storage details.",
    source: "companyInfo",
    fieldKeys: ["factorySize", "productionCapacity", "qualityCertifications"],
  },
  {
    id: "password",
    label: "Password",
    sellerOnly: true,
    sellerBlock: "password",
  },
];

/** Get sections to show for a role. Buyers (own profile) get BUYER_PROFILE_SECTIONS; sellers get Business Profile; others get PROFILE_SECTIONS. */
export function getSectionsForRole(
  role: "buyer" | "seller" | "admin",
  isViewingOtherUser: boolean,
  useBusinessProfileLayout?: boolean,
): ProfileSectionDef[] {
  if (role === "buyer" && !isViewingOtherUser) {
    return BUYER_PROFILE_SECTIONS;
  }
  const sections =
    useBusinessProfileLayout && (role === "seller" || (role === "admin" && isViewingOtherUser))
      ? BUSINESS_PROFILE_SECTIONS
      : PROFILE_SECTIONS;
  return sections.filter((s) => {
    if (s.sellerOnly && role !== "seller") return false;
    if (s.sellerOnly && isViewingOtherUser) return false;
    return true;
  });
}

/** Get default (first) section id for a role. */
export function getDefaultSectionId(
  role: "buyer" | "seller" | "admin",
  isViewingOtherUser: boolean,
  useBusinessProfileLayout?: boolean,
): string {
  const sections = getSectionsForRole(
    role,
    isViewingOtherUser,
    useBusinessProfileLayout,
  );
  if (role === "buyer") {
    const personal = sections.find((s) => s.id === "personal");
    return personal?.id ?? sections[0]?.id ?? "business-details";
  }
  return sections[0]?.id ?? "business-details";
}
